import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { endpoints } from '../../services/api';
import apiSafe from '../../services/apiSafe';
import { getImageUrl } from '../../utils/imageUrl';
import {
  FiBarChart,
  FiEdit2,
  FiEye,
  FiPlus,
  FiSearch,
  FiTrash2,
  FiUsers
} from '../../utils/icons';

interface Campaign {
  id: string;
  title: string;
  description: string;
  image_url?: string;
  image?: string; // For backwards compatibility
  startDate?: string;
  endDate?: string;
  status: 'active' | 'pending' | 'completed' | 'draft';
  current_participants?: number;
  participantsCount?: number; // For backwards compatibility
  votesCount?: number;
  goal?: number;
  targetAmount?: number; // For backwards compatibility
  raisedAmount?: number;
}

const CampaignList: React.FC = () => {
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchCampaigns();
  }, []);

  useEffect(() => {
    filterCampaigns();
  }, [campaigns, searchTerm, statusFilter]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);

      // Fetch campaigns from API
      const campaignsRes = await apiSafe.get(endpoints.campaigns.list);
      const participantsRes = await apiSafe.get(endpoints.participants.list).catch(() => ({ data: [] }));
      const votesRes = await apiSafe.get(endpoints.voting.votes).catch(() => ({ data: [] }));

      const campaignsData = Array.isArray(campaignsRes.data) ? campaignsRes.data : [];
      const participantsData = Array.isArray(participantsRes.data) ? participantsRes.data : [];
      const votesData = Array.isArray(votesRes.data) ? votesRes.data : [];

      // Count participants and votes per campaign
      const participantsByCampaign: { [key: string]: number } = {};
      const votesByCampaign: { [key: string]: number } = {};

      participantsData.forEach((participant: any) => {
        const campaignId = participant.campaign_id;
        if (campaignId) {
          participantsByCampaign[campaignId] = (participantsByCampaign[campaignId] || 0) + 1;
        }
      });

      votesData.forEach((vote: any) => {
        const participant = participantsData.find((p: any) => p.id === vote.participant_id);
        if (participant?.campaign_id) {
          votesByCampaign[participant.campaign_id] = (votesByCampaign[participant.campaign_id] || 0) + 1;
        }
      });

      // Map campaigns with real data
      const processedCampaigns: Campaign[] = campaignsData.map((campaign: any) => {
        const now = new Date();
        const startDate = campaign.start_date ? new Date(campaign.start_date) : now;
        const endDate = campaign.end_date ? new Date(campaign.end_date) : now;

        let status: 'active' | 'pending' | 'completed' = 'active';
        if (campaign.is_active === false || endDate < now) {
          status = 'completed';
        } else if (startDate > now) {
          status = 'pending';
        }

        return {
          id: campaign.id,
          title: campaign.title || 'Sem título',
          description: campaign.description || '',
          image: campaign.image_url || '/placeholder.jpg',
          startDate: campaign.start_date || new Date().toISOString(),
          endDate: campaign.end_date || new Date().toISOString(),
          status: status,
          participantsCount: participantsByCampaign[campaign.id] || 0,
          votesCount: votesByCampaign[campaign.id] || 0,
          targetAmount: campaign.target_amount || 0,
          raisedAmount: campaign.raised_amount || 0,
        };
      });

      setCampaigns(processedCampaigns);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      toast.error('Erro ao carregar campanhas');
    } finally {
      setLoading(false);
    }
  };

  const filterCampaigns = () => {
    let filtered = [...campaigns];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (campaign) =>
          campaign.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
          campaign.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((campaign) => campaign.status === statusFilter);
    }

    setFilteredCampaigns(filtered);
    setCurrentPage(1);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir esta campanha?')) {
      try {
        await apiSafe.delete(endpoints.campaigns.delete(id));
        setCampaigns(campaigns.filter((c) => c.id !== id));
        setFilteredCampaigns(filteredCampaigns.filter((c) => c.id !== id));
        toast.success('Campanha excluída com sucesso');
      } catch (error) {
        console.error('Error deleting campaign:', error);
        toast.error('Erro ao excluir campanha');
      }
    }
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      completed: 'bg-gray-100 text-gray-800',
    };
    const labels = {
      active: 'Ativa',
      pending: 'Pendente',
      completed: 'Encerrada',
    };
    return (
      <span
        className={`px-2 py-1 text-xs font-semibold rounded-full ${
          styles[status as keyof typeof styles]
        }`}
      >
        {labels[status as keyof typeof labels]}
      </span>
    );
  };

  const getProgressPercentage = (raised: number, target: number) => {
    return Math.min((raised / target) * 100, 100);
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredCampaigns.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Campanhas</h1>
          <p className="text-gray-600">Gerencie todas as campanhas da plataforma</p>
        </div>
        <button
          onClick={() => navigate('/campaigns/new')}
          className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-colors"
        >
          <FiPlus className="w-5 h-5 mr-2" />
          Nova Campanha
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-md p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar campanhas..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          >
            <option value="all">Todos os Status</option>
            <option value="active">Ativas</option>
            <option value="pending">Pendentes</option>
            <option value="completed">Encerradas</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Campanha
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Período
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Participantes
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Progresso
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {currentItems.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="h-10 w-10 flex-shrink-0">
                        <img
                          className="h-10 w-10 rounded-lg object-cover"
                          src={getImageUrl(campaign.image_url || campaign.image)}
                          alt={campaign.title}
                        />
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">{campaign.title}</div>
                        <div className="text-sm text-gray-500">{campaign.description}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900">
                      {campaign.startDate && campaign.endDate ? (
                        <>
                          {format(new Date(campaign.startDate), 'dd MMM', { locale: ptBR })} -{' '}
                          {format(new Date(campaign.endDate), 'dd MMM yyyy', { locale: ptBR })}
                        </>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">{getStatusBadge(campaign.status)}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center text-sm text-gray-900">
                      <FiUsers className="w-4 h-4 mr-1 text-gray-400" />
                      {campaign.current_participants || campaign.participantsCount || 0}
                    </div>
                    <div className="flex items-center text-sm text-gray-500 mt-1">
                      <FiBarChart className="w-4 h-4 mr-1 text-gray-400" />
                      {campaign.votesCount || 0} votos
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="w-32">
                      {campaign.goal ? (
                        <>
                          <div className="flex justify-between text-sm text-gray-600 mb-1">
                            <span>{campaign.current_participants || 0} participantes</span>
                            <span>{getProgressPercentage(campaign.current_participants || 0, campaign.goal).toFixed(0)}%</span>
                          </div>
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-gradient-to-r from-purple-500 to-indigo-500 h-2 rounded-full"
                              style={{
                                width: `${getProgressPercentage(
                                  campaign.current_participants || 0,
                                  campaign.goal
                                )}%`,
                              }}
                            ></div>
                          </div>
                          <div className="text-xs text-gray-500 mt-1">
                            Meta: {campaign.goal.toLocaleString()} participantes
                          </div>
                        </>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => navigate(`/campaigns/${campaign.id}`)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Visualizar"
                      >
                        <FiEye className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => navigate(`/campaigns/${campaign.id}/edit`)}
                        className="text-yellow-600 hover:text-yellow-900"
                        title="Editar"
                      >
                        <FiEdit2 className="w-5 h-5" />
                      </button>
                      <button
                        onClick={() => handleDelete(campaign.id)}
                        className="text-red-600 hover:text-red-900"
                        title="Excluir"
                      >
                        <FiTrash2 className="w-5 h-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 flex items-center justify-between">
            <div className="text-sm text-gray-700">
              Mostrando {indexOfFirstItem + 1} a {Math.min(indexOfLastItem, filteredCampaigns.length)} de{' '}
              {filteredCampaigns.length} resultados
            </div>
            <div className="flex space-x-2">
              <button
                onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Anterior
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1)
                .filter((page) => {
                  return (
                    page === 1 ||
                    page === totalPages ||
                    (page >= currentPage - 1 && page <= currentPage + 1)
                  );
                })
                .map((page, index, array) => (
                  <React.Fragment key={page}>
                    {index > 0 && array[index - 1] !== page - 1 && <span className="px-2">...</span>}
                    <button
                      onClick={() => setCurrentPage(page)}
                      className={`px-3 py-1 rounded ${
                        currentPage === page
                          ? 'bg-purple-600 text-white'
                          : 'border border-gray-300 hover:bg-gray-100'
                      }`}
                    >
                      {page}
                    </button>
                  </React.Fragment>
                ))}
              <button
                onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                className="px-3 py-1 rounded border border-gray-300 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-100"
              >
                Próximo
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CampaignList;
